/**
 * @module auth-storage
 * @description Authentication token storage utilities providing secure token management and retrieval functionality.
 */

import type { AuthTokens } from "../types";

/**
 * Storage keys for authentication data.
 * Provides namespaced keys for localStorage access.
 */
export const STORAGE_KEYS = {
  ACCESS_TOKEN: "taqat_access_token",
  REFRESH_TOKEN: "taqat_refresh_token",
  EXPIRES_AT: "taqat_expires_at",
  REFRESH_EXPIRES_AT: "taqat_refresh_expires_at",
  USER_DATA: "taqat_user_data",
  USER_ROLE: "taqat_user_role",
} as const;

/**
 * Unified user role.
 * 'admin' = workspace admin, 'user' = non-admin workspace member.
 */
export type UserRole = "admin" | "user";

/**
 * Checks if code is running in browser environment.
 * Prevents SSR errors when accessing localStorage.
 */
const isBrowser = typeof window !== "undefined";

/**
 * Whether this document is the embedded widget surface (`?embed=true`, set on
 * the iframe URL by the bubble loader / standalone embed).
 *
 * The embed iframe shares its ORIGIN — and therefore its `localStorage` — with
 * the host app whenever the widget is embedded on the same domain the platform
 * runs on (always true in local dev: iframe + app both on localhost:5173). If
 * the embed's per-visitor GUEST session were stored under the same keys as a
 * signed-in admin, it would clobber that admin session ("This embed session
 * cannot access the requested resource" in the main app). Namespacing the
 * embed's tokens keeps the two sessions isolated on a shared origin.
 *
 * Memoized: a document that boots as the embed surface stays the embed surface
 * for its whole lifetime, so in-iframe navigation that drops the query param
 * can never flip the namespace mid-session. The main app (no `embed` param)
 * memoizes `false` and its storage keys are entirely unchanged.
 */
let embedSurfaceMemo: boolean | null = null;
function isEmbedStorageSurface(): boolean {
  if (embedSurfaceMemo !== null) return embedSurfaceMemo;
  if (!isBrowser) return false;
  try {
    embedSurfaceMemo =
      new URLSearchParams(window.location.search).get("embed") === "true";
  } catch {
    embedSurfaceMemo = false;
  }
  return embedSurfaceMemo;
}

/**
 * Namespaces a storage key for the embed surface. No-op in the main app, so
 * signed-in sessions there read/write exactly the keys they always have.
 */
function scopedKey(base: string): string {
  return isEmbedStorageSurface() ? `embed_${base}` : base;
}

/**
 * User data interface for type safety.
 * Defines structure for stored user information.
 */
interface UserData {
  id: string;
  email: string;
  name?: string;
  role?: string;
  workspaceId?: string;
  [key: string]: unknown;
}

/**
 * Gets access token from storage.
 * Returns null if not found or in non-browser environment.
 * @returns Access token string or null if not found
 */
export function getAccessToken(): string | null {
  // Check browser environment
  if (!isBrowser) return null;

  try {
    // Retrieve token from localStorage
    const token = localStorage.getItem(scopedKey(STORAGE_KEYS.ACCESS_TOKEN));
    return token;
  } catch (error) {
    // Log storage access error
    console.warn("Failed to get access token from storage:", error);
    return null;
  }
}

/**
 * Gets refresh token from storage.
 * Returns null if not found or in non-browser environment.
 * @returns Refresh token string or null if not found
 */
export function getRefreshToken(): string | null {
  // Check browser environment
  if (!isBrowser) return null;

  try {
    // Retrieve refresh token from localStorage
    return localStorage.getItem(scopedKey(STORAGE_KEYS.REFRESH_TOKEN));
  } catch (error) {
    // Log storage access error
    console.warn("Failed to get refresh token from storage:", error);
    return null;
  }
}

/**
 * Gets token expiration timestamp from storage.
 * Parses stored string to number for date comparison.
 * @returns Expiration timestamp as number or null if not found
 */
export function getTokenExpiresAt(): number | null {
  // Check browser environment
  if (!isBrowser) return null;

  try {
    // Retrieve and parse expiration timestamp
    const expiresAt = localStorage.getItem(scopedKey(STORAGE_KEYS.EXPIRES_AT));
    // Parse string to number if exists
    return expiresAt ? parseInt(expiresAt, 10) : null;
  } catch (error) {
    // Log storage access error
    console.warn("Failed to get token expiration from storage:", error);
    return null;
  }
}

/**
 * Gets refresh token expiration timestamp from storage.
 * Parses stored string to number for date comparison.
 * @returns Refresh token expiration timestamp as number or null if not found
 */
export function getRefreshTokenExpiresAt(): number | null {
  // Check browser environment
  if (!isBrowser) return null;

  try {
    // Retrieve and parse refresh token expiration timestamp
    const refreshExpiresAt = localStorage.getItem(scopedKey(STORAGE_KEYS.REFRESH_EXPIRES_AT));
    // Parse string to number if exists
    return refreshExpiresAt ? parseInt(refreshExpiresAt, 10) : null;
  } catch (error) {
    // Log storage access error
    console.warn("Failed to get refresh token expiration from storage:", error);
    return null;
  }
}

/**
 * Gets all authentication tokens from storage.
 * Returns complete AuthTokens object or null if access token missing.
 * @returns AuthTokens object with tokens or null if access token missing
 */

/**
 * Stores authentication tokens in localStorage.
 * Saves access token, refresh token, and expiration timestamps.
 * @param tokens - AuthTokens object containing all token data
 */
export function setAuthTokens(tokens: AuthTokens): void {
  // Check browser environment
  if (!isBrowser) return;

  try {
    // Store each token component separately
    localStorage.setItem(scopedKey(STORAGE_KEYS.ACCESS_TOKEN), tokens.accessToken);

    // Only store refresh token if it exists
    if (tokens.refreshToken) {
      localStorage.setItem(scopedKey(STORAGE_KEYS.REFRESH_TOKEN), tokens.refreshToken);
    } else {
      // Remove any existing refresh token
      localStorage.removeItem(scopedKey(STORAGE_KEYS.REFRESH_TOKEN));
    }

    localStorage.setItem(scopedKey(STORAGE_KEYS.EXPIRES_AT), tokens.expiresAt.toString());

    // Store refresh token expiry if provided
    if (tokens.refreshExpiresAt) {
      localStorage.setItem(scopedKey(STORAGE_KEYS.REFRESH_EXPIRES_AT), tokens.refreshExpiresAt.toString());
    }
  } catch (error) {
    // Log storage write error
    console.error("Failed to store auth tokens:", error);
  }
}

/**
 * Updates only access token and expiration.
 * Used for token refresh without changing refresh token.
 * @param accessToken - New access token to store
 * @param expiresAt - New expiration timestamp
 */

/**
 * Removes all authentication tokens from storage.
 * Clears access token, refresh token, expiration, user data, and user type.
 */
export function clearAuthTokens(): void {
  // Check browser environment
  if (!isBrowser) return;

  try {
    // Remove all auth-related items from localStorage
    localStorage.removeItem(scopedKey(STORAGE_KEYS.ACCESS_TOKEN));
    localStorage.removeItem(scopedKey(STORAGE_KEYS.REFRESH_TOKEN));
    localStorage.removeItem(scopedKey(STORAGE_KEYS.EXPIRES_AT));
    localStorage.removeItem(scopedKey(STORAGE_KEYS.REFRESH_EXPIRES_AT));
    localStorage.removeItem(scopedKey(STORAGE_KEYS.USER_DATA));
    localStorage.removeItem(scopedKey(STORAGE_KEYS.USER_ROLE));
    // Defensive: drop legacy key from earlier versions
    localStorage.removeItem(scopedKey("taqat_user_type"));
  } catch (error) {
    // Log storage clear error
    console.error("Failed to clear auth tokens:", error);
  }
}

/**
 * Checks if access token exists and is valid.
 * Validates token presence and expiration with 5-minute buffer.
 * @returns True if token is valid, false otherwise
 */
export function isTokenValid(): boolean {
  // Get token and expiration
  const accessToken = getAccessToken();
  const expiresAt = getTokenExpiresAt();

  // Check if both token and expiration exist
  if (!accessToken || !expiresAt) {
    return false;
  }

  // Calculate expiration with buffer time
  const now = Date.now();
  const bufferTime = 2 * 60 * 1000; // 2 minutes buffer to allow time for refresh

  // Check if token expires after current time plus buffer
  return expiresAt > now + bufferTime;
}

/**
 * Checks if refresh token exists in storage.
 * Used to determine if token refresh is possible.
 * @returns True if refresh token exists, false otherwise
 */

/**
 * Checks if refresh token exists and is valid (not expired).
 * Used to determine if token refresh is possible.
 * @returns True if refresh token exists and is not expired, false otherwise
 */
export function isRefreshTokenValid(): boolean {
  // Get refresh token and expiration
  const refreshToken = getRefreshToken();
  const refreshExpiresAt = getRefreshTokenExpiresAt();

  // Check if both token and expiration exist
  if (!refreshToken || !refreshExpiresAt) {
    return false;
  }

  // Calculate expiration with small buffer time
  const now = Date.now();
  const bufferTime = 5 * 60 * 1000; // 5 minutes buffer for refresh token

  // Check if refresh token expires after current time plus buffer
  return refreshExpiresAt > now + bufferTime;
}

/**
 * Gets user data from storage.
 * Parses JSON string to UserData object.
 * @returns Parsed UserData object or null if not found
 */
export function getUserData(): UserData | null {
  // Check browser environment
  if (!isBrowser) return null;

  try {
    // Retrieve and parse user data from localStorage
    const userData = localStorage.getItem(scopedKey(STORAGE_KEYS.USER_DATA));
    // Parse JSON string if exists
    return userData ? (JSON.parse(userData) as UserData) : null;
  } catch (error) {
    // Log parse error
    console.warn("Failed to get user data from storage:", error);
    return null;
  }
}

/**
 * Stores user data in localStorage as JSON.
 * Serializes UserData object for storage.
 * @param userData - UserData object to store
 */
export function setUserData(userData: UserData): void {
  // Check browser environment
  if (!isBrowser) return;

  try {
    // Serialize and store user data
    localStorage.setItem(scopedKey(STORAGE_KEYS.USER_DATA), JSON.stringify(userData));
  } catch (error) {
    // Log storage write error
    console.error("Failed to store user data:", error);
  }
}

/**
 * Removes user data from storage.
 * Clears only user data, not authentication tokens.
 */
export function clearUserData(): void {
  // Check browser environment
  if (!isBrowser) return;

  try {
    // Remove user data from localStorage
    localStorage.removeItem(scopedKey(STORAGE_KEYS.USER_DATA));
  } catch (error) {
    // Log storage clear error
    console.error("Failed to clear user data:", error);
  }
}

/**
 * Checks if user is authenticated.
 * Returns true when the access token is valid OR when the refresh token is
 * still usable — an expired refresh token must NOT count as authenticated,
 * because we can't recover from it without re-signing in.
 * @returns True if user has a usable token path, false otherwise
 */
export function isAuthenticated(): boolean {
  return isTokenValid() || isRefreshTokenValid();
}

/**
 * Checks if the current session is an admin user session.
 * @returns True if the stored role is "admin"
 */
export function isAdminUser(): boolean {
  return getUserRole() === "admin";
}

/**
 * Gets the current user role from storage.
 * @returns "admin" | "user" | null
 */
export function getUserRole(): UserRole | null {
  if (!isBrowser) return null;

  try {
    const role = localStorage.getItem(scopedKey(STORAGE_KEYS.USER_ROLE));
    if (role === "admin" || role === "user") {
      return role;
    }
    return null;
  } catch (error) {
    return null;
  }
}

/**
 * Sets the current user role in storage.
 * @param role - "admin" | "user"
 */
export function setUserRole(role: UserRole): void {
  if (!isBrowser) return;

  try {
    localStorage.setItem(scopedKey(STORAGE_KEYS.USER_ROLE), role);
  } catch (error) {
    // Silent fail
  }
}

/**
 * Gets Authorization header value for API requests.
 * @returns Bearer token string or null if no token
 */
export function getAuthHeader(): string | null {
  const token = getAccessToken();
  return token ? `Bearer ${token}` : null;
}

/**
 * Storage utilities for debugging.
 * Development-only tools for inspecting auth state.
 */
